import { useEffect } from "react";

export function useFocusTrap(
    isActive: boolean,
    containerRef: React.RefObject<HTMLElement>
) {
    useEffect(() => {
        if (!isActive) return;

        const container = containerRef.current;
        if (!container) return;

        const focusableSelectors = [
            'a[href]',
            'button:not([disabled])',
            'textarea:not([disabled])',
            'input:not([disabled])',
            'select:not([disabled])',
            '[tabindex]:not([tabindex="-1"])'
        ];

        const getFocusableElements = () =>
            Array.from(container.querySelectorAll<HTMLElement>(focusableSelectors.join(",")))
                .filter(el => el.offsetParent !== null);

        const focusableElements = getFocusableElements();
        if (focusableElements.length === 0) return;

        const first = focusableElements[0];
        const last = focusableElements[focusableElements.length - 1];

        first.focus();

        const handleKeyDown = (e: KeyboardEvent) => {
            if (e.key === "Escape") {
                container.dispatchEvent(new CustomEvent("escape"));
                return;
            }

            if (e.key !== "Tab") return;

            if (focusableElements.length === 0) return;

            if (e.shiftKey) {
                if (document.activeElement === first) {
                    e.preventDefault();
                    last.focus();
                }
            } else {
                if (document.activeElement === last) {
                    e.preventDefault();
                    first.focus();
                }
            }
        };

        document.addEventListener("keydown", handleKeyDown);

        return () => {
            document.removeEventListener("keydown", handleKeyDown);
        };
    }, [isActive, containerRef]);
}
